import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Modal, Spin, Table, message } from "antd";
import {
  applyGridSettings,
  loadGridSettings,
  normalizeGridSettings,
  saveGridSettings
} from "@controls/grid-settings";
import { deleteDocPlat, fetchDocPlatList, fetchDocPlatLookups } from "./doc-plat-api";
import DocPlatEditDrawer from "./doc-plat-edit-drawer";
import { buildColumns, buildDefaultGridSettings } from "./doc-plats-columns";
import DocPlatsFilters from "./doc-plats-filters";
import {
  GRID_STORAGE_KEY,
  filtersFromSearchParams,
  filtersSearchEquals,
  filtersToSearchParams,
  getLawFlag,
  loadStoredFilters,
  measurePlatAutoColWidths,
  normalizeListRow,
  normalizeLookups,
  pick,
  saveFilters
} from "./doc-plat-utils";
import "./doc-plats.css";

const PAGE_SIZE = 50;
const SCROLL_LOAD_GAP = 80;
const TABLE_HEADER_OFFSET = 32;
const SEARCH_DEBOUNCE_MS = 350;

const normalizePage = (data) => {
  const items = Array.isArray(data)
    ? data
    : Array.isArray(pick(data, "items", "Items"))
      ? pick(data, "items", "Items")
      : [];
  const hasMore = Array.isArray(data)
    ? items.length >= PAGE_SIZE
    : Boolean(pick(data, "hasMore", "HasMore"));
  return {
    items: items.map(normalizeListRow).filter((r) => r.idReg != null),
    hasMore
  };
};

/**
 * Журнал платежей. Delphi: TFrmJurnPlat.
 */
const DocPlats = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const law = useSelector((state) => state.user.session?.law);
  const canView = getLawFlag(law, 13) === 1;
  const canAdd = getLawFlag(law, 36) === 1;
  const canDelete = getLawFlag(law, 37) === 1;
  const canEditLaw = getLawFlag(law, 38) === 1;

  const [filters, setFilters] = useState(() => {
    const fromUrl = filtersFromSearchParams(
      typeof window !== "undefined" ? window.location.search : ""
    );
    return fromUrl || loadStoredFilters();
  });
  const [debouncedQ, setDebouncedQ] = useState(() => (filters.q || "").trim());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const [tableScrollY, setTableScrollY] = useState(400);
  const [gridSettings, setGridSettings] = useState(() => loadGridSettings(GRID_STORAGE_KEY));
  const [editOpen, setEditOpen] = useState(false);
  const [editIsNew, setEditIsNew] = useState(false);
  const [editId, setEditId] = useState(null);
  const [kassas, setKassas] = useState([]);

  const requestIdRef = useRef(0);
  const skipRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listBodyRef = useRef(null);
  const loadPageRef = useRef(null);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    saveFilters();
  }, []);

  useEffect(() => {
    const next = filtersToSearchParams(filters);
    const nextStr = next.toString();
    if (searchParams.toString() === nextStr) return undefined;
    const delay = filters.q !== debouncedQ ? SEARCH_DEBOUNCE_MS : 0;
    const t = setTimeout(() => {
      skipUrlSyncRef.current = true;
      setSearchParams(next, { replace: true });
    }, delay);
    return () => clearTimeout(t);
  }, [filters, debouncedQ, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = filtersFromSearchParams(searchParams);
    if (!fromUrl) return;
    setFilters((prev) => (filtersSearchEquals(fromUrl, prev) ? prev : fromUrl));
    setDebouncedQ((fromUrl.q || "").trim());
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((filters.q || "").trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filters.q]);

  useEffect(() => {
    if (!canView) return;
    fetchDocPlatLookups()
      .then((data) => {
        setKassas(normalizeLookups(data).kassas);
      })
      .catch(() => {
        setKassas([]);
      });
  }, [canView]);

  const loadPage = useCallback(
    ({ reset }) => {
      if (!reset && (loadingMoreRef.current || !hasMoreRef.current)) return;

      const requestId = ++requestIdRef.current;
      const skip = reset ? 0 : skipRef.current;

      if (reset) {
        setLoading(true);
        setError("");
        const tableBody = listBodyRef.current?.querySelector(".ant-table-body");
        if (tableBody) tableBody.scrollTop = 0;
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      fetchDocPlatList({
        dateBeg: filters.dateBeg,
        dateEnd: filters.dateEnd,
        sortBy: filters.sortBy || "fdate",
        sortDir: filters.sortDir || "desc",
        showDeleted: Boolean(filters.showDeleted),
        idKassa: filters.idKassa || 0,
        vid: filters.vid || 0,
        q: debouncedQ || undefined,
        skip,
        take: PAGE_SIZE
      })
        .then((data) => {
          if (requestId !== requestIdRef.current) return;
          const page = normalizePage(data);
          setRows((prev) => (reset ? page.items : [...prev, ...page.items]));
          if (reset) setCheckedIds([]);
          skipRef.current = skip + page.items.length;
          hasMoreRef.current = page.hasMore;
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          if (reset) {
            setError(e.message || "Не удалось загрузить журнал");
            setRows([]);
            skipRef.current = 0;
            hasMoreRef.current = false;
          } else {
            message.error(e.message || "Не удалось подгрузить записи");
          }
        })
        .finally(() => {
          if (requestId !== requestIdRef.current) return;
          if (reset) setLoading(false);
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
    },
    [filters.dateBeg, filters.dateEnd, filters.sortBy, filters.sortDir, filters.showDeleted, filters.idKassa, filters.vid, debouncedQ]
  );

  useEffect(() => {
    if (!canView) return;
    skipRef.current = 0;
    hasMoreRef.current = false;
    loadPage({ reset: true });
  }, [canView, filters.dateBeg, filters.dateEnd, filters.sortBy, filters.sortDir, filters.showDeleted, filters.idKassa, filters.vid, debouncedQ, loadPage]);

  loadPageRef.current = loadPage;

  useEffect(() => {
    const el = listBodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      const h = el.clientHeight;
      setTableScrollY(Math.max(160, h - TABLE_HEADER_OFFSET));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [canView, loading, rows.length, error]);

  useEffect(() => {
    const root = listBodyRef.current;
    if (!root) return undefined;
    const body = root.querySelector(".ant-table-body");
    if (!body) return undefined;

    const onScroll = () => {
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - SCROLL_LOAD_GAP) {
        loadPageRef.current?.({ reset: false });
      }
    };

    body.addEventListener("scroll", onScroll);
    return () => body.removeEventListener("scroll", onScroll);
  }, [rows.length, tableScrollY, loading]);

  const reload = useCallback(() => {
    skipRef.current = 0;
    hasMoreRef.current = false;
    loadPage({ reset: true });
  }, [loadPage]);

  const onTableChange = useCallback((_pagination, _tableFilters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!s?.order) {
      setFilters((prev) => ({ ...prev, sortBy: "fdate", sortDir: "desc" }));
      return;
    }
    const sortBy = s.columnKey || s.field;
    if (!sortBy) return;
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortDir: s.order === "descend" ? "desc" : "asc"
    }));
  }, []);

  const checkedRows = useMemo(
    () => rows.filter((r) => checkedIds.includes(r.idReg) && !r.isDel),
    [rows, checkedIds]
  );
  const canDeleteChecked = canDelete && checkedRows.length > 0;

  const openCreate = () => {
    if (!canAdd) return;
    setEditIsNew(true);
    setEditId(null);
    setEditOpen(true);
  };

  const openEdit = (record) => {
    if (!record?.idReg || record.isDel) return;
    if (!canEditLaw) return;
    setSelectedId(record.idReg);
    setEditIsNew(false);
    setEditId(record.idReg);
    setEditOpen(true);
  };

  const onDelete = () => {
    if (!canDeleteChecked) return;
    const ids = checkedRows.map((r) => r.idReg);
    const many = ids.length > 1;
    const label = (r) => (r.docnum ? `№ ${r.docnum}` : `#${r.idReg}`);
    Modal.confirm({
      title: many ? `Удалить платежи (${ids.length})?` : "Удалить платёж?",
      content: many ? checkedRows.map(label).join(", ") : label(checkedRows[0]),
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          for (const id of ids) {
            await deleteDocPlat(id);
          }
          message.success("Удалено");
          setCheckedIds([]);
          if (ids.includes(selectedId)) setSelectedId(null);
          if (ids.includes(editId)) setEditOpen(false);
          reload();
        } catch (e) {
          message.error(e.message || "Не удалось удалить");
          reload();
          throw e;
        }
      }
    });
  };

  const autoWidths = useMemo(() => measurePlatAutoColWidths(rows), [rows]);

  const allColumns = useMemo(
    () =>
      buildColumns({
        sortBy: filters.sortBy || "fdate",
        sortDir: filters.sortDir || "desc",
        autoWidths
      }),
    [filters.sortBy, filters.sortDir, autoWidths]
  );
  const gridDefaultSettings = useMemo(
    () => buildDefaultGridSettings(allColumns),
    [allColumns]
  );
  const resolvedGridSettings = useMemo(
    () => normalizeGridSettings(allColumns, gridSettings || gridDefaultSettings),
    [allColumns, gridSettings, gridDefaultSettings]
  );
  const columns = useMemo(
    () => applyGridSettings(allColumns, resolvedGridSettings),
    [allColumns, resolvedGridSettings]
  );
  const onGridSettingsChange = useCallback(
    (next) => {
      const normalized = normalizeGridSettings(
        buildColumns({
          sortBy: filters.sortBy || "fdate",
          sortDir: filters.sortDir || "desc",
          autoWidths
        }),
        next
      );
      setGridSettings(normalized);
      saveGridSettings(GRID_STORAGE_KEY, normalized);
    },
    [filters.sortBy, filters.sortDir, autoWidths]
  );

  const tableScrollX = useMemo(
    () =>
      Math.max(
        960,
        columns.reduce((sum, c) => sum + (Number(c.width) || 0), 0) + (canDelete ? 36 : 0)
      ),
    [columns, canDelete]
  );

  const rowSelection = useMemo(
    () =>
      canDelete
        ? {
            selectedRowKeys: checkedIds,
            columnWidth: 36,
            getCheckboxProps: (record) => ({ disabled: Boolean(record.isDel) }),
            onChange: (keys) => {
              setCheckedIds(keys.map(Number).filter(Number.isFinite));
            }
          }
        : undefined,
    [canDelete, checkedIds]
  );

  if (!canView) {
    return (
      <div className="doc-plats">
        <Alert type="warning" showIcon message="Нет права просмотра журнала платежей" />
      </div>
    );
  }

  return (
    <div className="doc-plats">
      <DocPlatsFilters
        value={filters}
        loading={loading}
        canAdd={canAdd}
        canDelete={canDeleteChecked}
        reportId={selectedId}
        onChange={setFilters}
        onCreate={openCreate}
        onDelete={onDelete}
        gridColumns={allColumns}
        gridStorageKey={GRID_STORAGE_KEY}
        gridSettings={resolvedGridSettings}
        gridDefaultSettings={gridDefaultSettings}
        onGridSettingsChange={onGridSettingsChange}
        kassas={kassas}
      />
      {error ? (
        <Alert className="doc-plats__error" type="error" showIcon message={error} />
      ) : null}
      <div ref={listBodyRef} className="doc-plats__body">
        {loading && rows.length === 0 ? (
          <div className="doc-plats__center">
            <Spin />
          </div>
        ) : (
          <div
            className="doc-plats__table-wrap"
            style={{ "--doc-plats-table-x": `${tableScrollX}px` }}
          >
            <Table
              className="doc-plats__table"
              size="small"
              rowKey="idReg"
              columns={columns}
              dataSource={rows}
              pagination={false}
              loading={loadingMore}
              rowSelection={rowSelection}
              locale={{ emptyText: "Нет платежей" }}
              scroll={{ x: tableScrollX, y: tableScrollY }}
              sortDirections={["ascend", "descend"]}
              onChange={onTableChange}
              onRow={(record) => ({
                onClick: (e) => {
                  if (e.target.closest(".ant-checkbox-wrapper, .ant-table-selection-column")) {
                    return;
                  }
                  setSelectedId(record.idReg);
                  openEdit(record);
                },
                className: [
                  record.isDel ? "doc-plats__row--deleted" : "",
                  record.idReg === selectedId ? "doc-plats__row--selected" : ""
                ]
                  .filter(Boolean)
                  .join(" ")
              })}
            />
          </div>
        )}
      </div>
      <DocPlatEditDrawer
        open={editOpen}
        idReg={editId}
        isNew={editIsNew}
        law={law}
        onCancel={() => setEditOpen(false)}
        onDone={() => {
          setEditOpen(false);
          reload();
        }}
      />
    </div>
  );
};

export default DocPlats;
